import { createTheme } from '@mui/material/styles';
import { colors } from './colors';
import { fontFamilies, fontWeights, fontSizes, spacing, borderRadius, shadows, buttonStyles } from './tokens';

export const createAppTheme = (themeMode: 'light' | 'dark' | 'retro') => {
  const isDarkMode = themeMode === 'dark';
  const isRetro = themeMode === 'retro';
  
  // App font stack (Montserrat loaded via Google Fonts)
  const hkFontFamily = fontFamilies.marketing;
  const hkMonoFamily = fontFamilies.mono;
  
  return createTheme({
  palette: {
    mode: isRetro ? 'dark' : (isDarkMode ? 'dark' : 'light'),
    primary: {
      // In dark mode, use light blue for readability on dark backgrounds
      main: isRetro ? '#00ff00' : (isDarkMode ? colors.primary.light : colors.primary.main),
      light: isRetro ? '#33ff33' : colors.primary.light,
      dark: isRetro ? '#00cc00' : (isDarkMode ? colors.primary.main : colors.primary.dark),
    },
    secondary: {
      main: isRetro ? '#00cccc' : colors.secondary.main,
      light: isRetro ? '#33ffff' : colors.secondary.light,
      dark: isRetro ? '#009999' : colors.secondary.dark,
    },
    success: {
      main: isRetro ? '#00ff00' : colors.success.main,
      light: isRetro ? '#33ff33' : colors.success.light,
      dark: isRetro ? '#00cc00' : colors.success.dark,
    },
    warning: {
      main: isRetro ? '#ffff00' : colors.warning.main,
      light: isRetro ? '#ffff66' : colors.warning.bright,
    },
    error: {
      main: isRetro ? '#ff0000' : colors.error.main,
      light: isRetro ? '#ff6666' : colors.error.light,
      dark: isRetro ? '#cc0000' : colors.error.dark,
    },
    grey: {
      50: isRetro ? '#001100' : '#f5f5f5',
      100: isRetro ? '#002200' : '#eeeeee',
      200: isRetro ? '#003300' : '#e0e0e0',
      300: isRetro ? '#004400' : '#bdbdbd',
      400: isRetro ? '#005500' : '#9e9e9e',
      500: isRetro ? '#006600' : '#757575',
      600: isRetro ? '#007700' : '#616161',
      700: isRetro ? '#008800' : '#424242',
      800: isRetro ? '#009900' : '#303030',
      900: isRetro ? '#00aa00' : '#212121',
    },
    background: {
      default: isRetro ? '#000000' : (isDarkMode ? '#0A1929' : '#FFFFFF'),
      paper: isRetro ? '#001a00' : (isDarkMode ? '#132F4C' : '#FFFFFF'),
    },
    text: {
      primary: isRetro ? '#00ff00' : (isDarkMode ? '#FFFFFF' : colors.primary.main),
      secondary: isRetro ? '#00cc00' : (isDarkMode ? '#A6BBC8' : 'rgba(1, 33, 105, 0.7)'),
    },
  },
  spacing: 8, // Base spacing unit (MUI default, use tokens for specific values)
  shape: {
    borderRadius: borderRadius.md,
  },
  typography: {
    fontFamily: isRetro ? '"Courier New", Monaco, Consolas, monospace' : hkFontFamily,
    h1: { 
      fontSize: fontSizes.h1, 
      fontWeight: fontWeights.bold, 
      lineHeight: 1.2,
      color: isRetro ? '#00ff00' : (isDarkMode ? '#ffffff' : colors.primary.main) 
    },
    h2: { 
      fontSize: fontSizes.h2, 
      fontWeight: fontWeights.semibold, 
      lineHeight: 1.2,
      color: isRetro ? '#00ff00' : (isDarkMode ? '#ffffff' : colors.primary.main) 
    },
    h3: { 
      fontSize: fontSizes.h3, 
      fontWeight: fontWeights.semibold, 
      lineHeight: 1.3,
      color: isRetro ? '#00ff00' : (isDarkMode ? '#ffffff' : colors.primary.main) 
    },
    h4: { 
      fontSize: fontSizes.h4, 
      fontWeight: fontWeights.medium, 
      lineHeight: 1.4,
      color: isRetro ? '#00ff00' : (isDarkMode ? '#ffffff' : colors.primary.main) 
    },
    h5: { 
      fontSize: fontSizes.bodyLarge, 
      fontWeight: fontWeights.medium, 
      color: isRetro ? '#00ff00' : (isDarkMode ? '#ffffff' : colors.primary.main) 
    },
    h6: { 
      fontSize: fontSizes.body, 
      fontWeight: fontWeights.semibold, 
      color: isRetro ? '#00ff00' : (isDarkMode ? '#ffffff' : colors.primary.main) 
    },
    body1: {
      fontSize: fontSizes.body,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: fontSizes.bodySmall,
      lineHeight: 1.5,
    },
    caption: {
      fontSize: fontSizes.caption,
      lineHeight: 1.4,
    },
    overline: {
      fontSize: fontSizes.overline,
      fontWeight: fontWeights.semibold,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    button: {
      fontSize: fontSizes.bodySmall,
      fontWeight: fontWeights.semibold,
      textTransform: 'none',
    },
  },
  components: {
    // Note: Montserrat is loaded via public/index.html <link> tag
    MuiCssBaseline: {
      styleOverrides: {
        // Global baseline styles if needed
        body: {
          scrollbarWidth: 'thin',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: isRetro ? '#000000' : colors.primary.main,
          borderBottom: isRetro ? '2px solid #00ff00' : 'none',
          boxShadow: isRetro ? '0 0 20px #00ff00' : shadows.md,
          borderRadius: 0,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: isRetro ? '#00ff00' : (isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit'),
          textShadow: isRetro ? '0 0 8px #00ff00' : 'none',
          '&.MuiTypography-root[href]': {
            transition: 'text-shadow 0.2s ease',
            '&:hover': {
              textShadow: isRetro ? '0 0 12px #00ff00' : '0 0 8px rgba(255, 255, 255, 0.6)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fontWeights.semibold,
          fontSize: fontSizes.bodySmall,
          borderRadius: borderRadius.md,
          padding: '12px 24px',
          border: '2px solid transparent',
          transition: 'all 0.2s ease',
          '&:focus-visible': {
            outline: `2px solid ${colors.secondary.main}`,
            outlineOffset: '2px',
          },
          '&:hover': {
            textShadow: isRetro ? '0 0 8px #00ff00' : 'none',
          },
        },
        contained: {
          backgroundColor: isRetro ? '#003300' : colors.primary.main,
          color: isRetro ? '#00ff00' : '#FFFFFF',
          border: isRetro ? '1px solid #00ff00' : 'none',
          boxShadow: isRetro ? '0 0 15px #00ff00' : shadows.sm,
          '&:hover': {
            backgroundColor: isRetro ? '#004400' : colors.secondary.main,
            boxShadow: isRetro ? '0 0 20px #00ff00' : shadows.md,
          },
          '&:active': {
            backgroundColor: isRetro ? '#002200' : colors.secondary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: isRetro ? '#003333' : colors.secondary.main,
          '&:hover': {
            backgroundColor: isRetro ? '#004444' : colors.secondary.dark,
          },
        },
        outlined: {
          border: `2px solid ${isRetro ? '#00ff00' : colors.primary.main}`,
          color: isRetro ? '#00ff00' : colors.primary.main,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: isRetro ? '#001100' : '#F8FAFB',
            border: `2px solid ${isRetro ? '#33ff33' : colors.primary.main}`,
          },
          '&:active': {
            backgroundColor: isRetro ? '#002200' : '#F0F4F7',
          },
        },
        text: {
          color: isRetro ? '#00ff00' : colors.secondary.main,
          '&:hover': {
            backgroundColor: isRetro ? '#001100' : '#F8FAFB',
          },
        },
        sizeSmall: {
          padding: '8px 16px',
          fontSize: fontSizes.caption,
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: fontSizes.body,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: isRetro ? '0 0 25px #00ff00' : (isDarkMode ? '0 4px 12px rgba(0,0,0,0.4)' : shadows.md),
          backgroundColor: isRetro ? '#001a00' : (isDarkMode ? '#132F4C' : '#ffffff'),
          border: isRetro ? '1px solid #00ff00' : (isDarkMode ? '1px solid rgba(255,255,255,0.1)' : 'none'),
          borderRadius: borderRadius.md,
          transition: 'box-shadow 0.2s ease',
          '&.MuiPaper-root[role="button"], &.MuiCard-root': {
            cursor: 'pointer',
            '&:hover': {
              boxShadow: isRetro ? '0 0 35px #00ff00' : (isDarkMode ? '0 6px 20px rgba(0,0,0,0.6)' : `0 4px 16px ${colors.primary.main}2A`),
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: fontWeights.medium,
          fontSize: fontSizes.caption,
          borderRadius: borderRadius.sm,
          border: isRetro ? '1px solid #00ff00' : undefined,
          backgroundColor: isRetro ? '#002200' : undefined,
          color: isRetro ? '#00ff00' : undefined,
          '&.MuiChip-colorPrimary': {
            backgroundColor: isRetro ? '#003300' : colors.primary.main,
            color: isRetro ? '#00ff00' : '#FFFFFF',
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: isRetro ? '#003333' : colors.secondary.main,
            color: isRetro ? '#00ff00' : colors.primary.main, // Dark text for WCAG AA compliance
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: isRetro ? '#003300' : colors.success.main,
            color: isRetro ? '#00ff00' : '#FFFFFF',
          },
          '&.MuiChip-colorError': {
            backgroundColor: isRetro ? '#330000' : colors.error.main,
            color: isRetro ? '#ff0000' : '#FFFFFF',
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: isRetro ? '#333300' : colors.warning.main,
            color: isRetro ? '#ffff00' : colors.primary.main,
          },
          '&.MuiChip-outlined': {
            backgroundColor: 'transparent',
            borderWidth: 1.5,
          },
          '&.MuiChip-outlined.MuiChip-colorPrimary': {
            color: isRetro ? '#00ff00' : colors.primary.main,
            borderColor: isRetro ? '#00ff00' : colors.primary.main,
          },
          '&.MuiChip-outlined.MuiChip-colorSecondary': {
            color: isRetro ? '#00ff00' : colors.secondary.main,
            borderColor: isRetro ? '#00ff00' : colors.secondary.main,
          },
          '&.MuiChip-outlined.MuiChip-colorSuccess': {
            color: isRetro ? '#00ff00' : colors.success.main,
            borderColor: isRetro ? '#00ff00' : colors.success.main,
          },
          '&.MuiChip-outlined.MuiChip-colorError': {
            color: isRetro ? '#ff0000' : colors.error.main,
            borderColor: isRetro ? '#ff0000' : colors.error.main,
          },
          '&.MuiChip-outlined.MuiChip-colorWarning': {
            color: isRetro ? '#ffff00' : colors.warning.main,
            borderColor: isRetro ? '#ffff00' : colors.warning.main,
          },
        },
        sizeSmall: {
          fontSize: fontSizes.overline,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: isRetro ? '#002200' : undefined,
          border: isRetro ? '1px solid #00ff00' : undefined,
          height: isRetro ? '20px' : undefined,
          '& .MuiLinearProgress-bar': {
            backgroundColor: isRetro ? 'transparent' : undefined,
            backgroundImage: isRetro ? 'url(/src/components/retro.png)' : undefined,
            backgroundSize: isRetro ? '16px 16px' : undefined,
            backgroundRepeat: isRetro ? 'no-repeat' : undefined,
            backgroundPosition: isRetro ? 'right center' : undefined,
            boxShadow: isRetro ? '0 0 10px #00ff00' : undefined,
            animation: isRetro ? 'retroProgress 2s ease-in-out infinite' : undefined,
          },
          '@keyframes retroProgress': isRetro ? {
            '0%': { backgroundPosition: 'left center' },
            '100%': { backgroundPosition: 'right center' }
          } : undefined,
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-label': {
            color: isRetro ? '#00ff00' : undefined,
            '&.Mui-active': {
              color: isRetro ? '#00ff00' : undefined,
              textShadow: isRetro ? '0 0 8px #00ff00' : undefined,
            },
            '&.Mui-completed': {
              color: isRetro ? '#00cc00' : undefined,
            },
          },
          '& .MuiStepIcon-root': {
            color: isRetro ? '#004400' : undefined,
            '&.Mui-active': {
              color: isRetro ? '#00ff00' : undefined,
              filter: isRetro ? 'drop-shadow(0 0 8px #00ff00)' : undefined,
            },
            '&.Mui-completed': {
              color: isRetro ? '#00cc00' : undefined,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.md,
            '& fieldset': {
              borderWidth: 1.5,
              borderColor: isRetro ? '#00ff00' : (isDarkMode ? 'rgba(255,255,255,0.23)' : colors.neutral.medium),
            },
            '&:hover fieldset': {
              borderColor: isRetro ? '#33ff33' : (isDarkMode ? 'rgba(255,255,255,0.5)' : colors.primary.light),
            },
            '&.Mui-focused fieldset': {
              borderColor: isRetro ? '#00ff00' : colors.primary.light,
              borderWidth: 2,
              boxShadow: isRetro ? '0 0 10px #00ff00' : `0 0 0 3px ${colors.primary.light}20`,
            },
            '&.Mui-error fieldset': {
              borderColor: colors.error.main,
            },
            '&.Mui-error.Mui-focused fieldset': {
              boxShadow: `0 0 0 3px ${colors.error.main}20`,
            },
          },
          '& .MuiInputLabel-root': {
            color: isRetro ? '#00ff00' : (isDarkMode ? 'rgba(255,255,255,0.7)' : colors.neutral.dark),
            '&.Mui-focused': {
              color: isRetro ? '#00ff00' : colors.primary.light,
            },
            '&.Mui-error': {
              color: colors.error.main,
            },
          },
          '& .MuiOutlinedInput-input': {
            color: isRetro ? '#00ff00' : undefined,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isRetro ? '#00ff00' : undefined,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isRetro ? '#33ff33' : undefined,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: isRetro ? '#00ff00' : undefined,
            boxShadow: isRetro ? '0 0 10px #00ff00' : undefined,
          },
        },
        icon: {
          color: isRetro ? '#00ff00' : undefined,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: isRetro ? '#001a00' : (isDarkMode ? '#132F4C' : '#FFFFFF'),
          border: isRetro ? '1px solid #00ff00' : undefined,
          boxShadow: isRetro ? '0 0 25px #00ff00' : shadows.lg,
          borderRadius: borderRadius.md,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: isRetro ? '#00ff00' : undefined,
          fontSize: fontSizes.bodySmall,
          '&:hover': {
            backgroundColor: isRetro ? '#003300' : (isDarkMode ? 'rgba(255,255,255,0.08)' : '#F8FAFB'),
            textShadow: isRetro ? '0 0 8px #00ff00' : undefined,
          },
          '&.Mui-selected': {
            backgroundColor: isRetro ? '#004400' : (isDarkMode ? 'rgba(0,169,224,0.16)' : `${colors.secondary.main}15`),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: isRetro ? '0 0 25px #00ff00' : shadows.md,
          borderRadius: borderRadius.md,
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: isRetro ? '0 0 35px #00ff00' : shadows.lg,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacing.md,
          '&:last-child': {
            paddingBottom: spacing.md,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          fontSize: fontSizes.bodySmall,
        },
        standardInfo: {
          backgroundColor: isDarkMode ? 'rgba(0,169,224,0.15)' : '#E0F4F9',
          color: isDarkMode ? colors.secondary.light : colors.secondary.dark,
        },
        standardSuccess: {
          backgroundColor: isDarkMode ? 'rgba(80,166,132,0.15)' : '#E8F5E9',
          color: isDarkMode ? colors.success.light : colors.success.dark,
        },
        standardWarning: {
          backgroundColor: isDarkMode ? 'rgba(207,176,35,0.15)' : '#FFF8E1',
          color: isDarkMode ? colors.warning.bright : '#7A5D00',
        },
        standardError: {
          backgroundColor: isDarkMode ? 'rgba(138,21,56,0.15)' : '#FFEBEE',
          color: isDarkMode ? colors.error.light : colors.error.main,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          backgroundColor: isRetro ? '#00ff00' : colors.secondary.main,
          height: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: fontWeights.medium,
          fontSize: fontSizes.bodySmall,
          minHeight: 44,
          '&.Mui-selected': {
            color: isRetro ? '#00ff00' : colors.secondary.main,
            fontWeight: fontWeights.semibold,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadius.lg,
          boxShadow: shadows.xl,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: fontSizes.h4,
          fontWeight: fontWeights.semibold,
          padding: `${spacing.md}px ${spacing.md}px ${spacing.sm}px`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: `${spacing.sm}px ${spacing.md}px`,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${spacing.sm}px ${spacing.md}px ${spacing.md}px`,
          gap: spacing.sm,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: isDarkMode ? '#1A3A52' : '#F8FAFB',
            fontWeight: fontWeights.semibold,
            fontSize: fontSizes.caption,
            color: isDarkMode ? '#FFFFFF' : colors.primary.main,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:nth-of-type(odd)': {
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(1,33,105,0.02)',
            },
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(1,33,105,0.04)',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: fontSizes.bodySmall,
          padding: `${spacing.sm}px`,
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(1,33,105,0.1)'}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: isDarkMode ? colors.primary.main : colors.neutral.darkest,
          fontSize: fontSizes.caption,
          borderRadius: borderRadius.sm,
          padding: '6px 12px',
        },
        arrow: {
          color: isDarkMode ? colors.primary.main : colors.neutral.darkest,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: colors.neutral.medium,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: isRetro ? '#00ff00' : colors.secondary.main,
          textDecoration: 'none',
          '&:hover': {
            color: isRetro ? '#33ff33' : colors.secondary.dark,
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary.light,
          color: '#FFFFFF',
          fontWeight: fontWeights.semibold,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: isRetro ? '#00ff00' : colors.secondary.main,
            '& + .MuiSwitch-track': {
              backgroundColor: isRetro ? '#00ff00' : colors.secondary.main,
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: isRetro ? '#00ff00' : colors.neutral.medium,
          '&.Mui-checked': {
            color: isRetro ? '#00ff00' : colors.secondary.main,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: isRetro ? '#00ff00' : colors.neutral.medium,
          '&.Mui-checked': {
            color: isRetro ? '#00ff00' : colors.secondary.main,
          },
        },
      },
    },
  },
  });
};